/**
 * Guest-session client (tiers T7/T8, Beta-gated). Mints the reCAPTCHA-gated
 * 24h guest token via `POST /guest/start` and keeps the guest IDENTITY in
 * localStorage — reloads and new tabs present the SAME guestId, so the
 * server-side daily budget follows the browser instead of resetting per
 * tab (user report). Conversations stay per-visit (the chat session id is
 * not persisted); the token authorizes ONLY the guest invoke proxy, and
 * storage-clearing evasion is watched by the mint-velocity telemetry
 * (FR-GUEST-5).
 */
import { guestConfig } from '../config/agent-config.ts';
import { getRecaptchaToken } from '../auth/recaptcha.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

export interface GuestSession {
  readonly token: string;
  readonly guestId: string;
  /** ISO instant the token expires (≤24h from mint). */
  readonly expiresAt: string;
}

const STORAGE_KEY = 'om-guest-session';
const PROMPT_KEY = 'om-guest-pending-prompt';

export class GuestStartError extends Error {
  /** True when the reCAPTCHA screen flagged the attempt (403). */
  public readonly flagged: boolean;

  public constructor(message: string, flagged: boolean) {
    super(message);
    this.name = 'GuestStartError';
    this.flagged = flagged;
  }
}

/** Mint a fresh guest session (one reCAPTCHA assessment per mint). */
export async function startGuestSession(): Promise<GuestSession> {
  const recaptchaToken = await getRecaptchaToken('guest_start');
  const res = await fetch(guestConfig.startUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ recaptchaToken }),
    signal: requestTimeout(),
  });
  if (!res.ok) {
    // 403 = flagged as automated traffic; anything else is a service fault.
    throw new GuestStartError(
      res.status === 403
        ? 'This request was flagged as potential automated traffic. Try again in a moment, or create a free account.'
        : 'Guest chat is temporarily unavailable. Please try again shortly.',
      res.status === 403,
    );
  }
  const body = (await res.json()) as {
    token?: string;
    guestId?: string;
    expiresAt?: string;
  };
  if (
    typeof body.token !== 'string' ||
    typeof body.guestId !== 'string' ||
    typeof body.expiresAt !== 'string'
  ) {
    throw new GuestStartError('Guest chat is temporarily unavailable. Please try again shortly.', false);
  }
  const session: GuestSession = {
    token: body.token,
    guestId: body.guestId,
    expiresAt: body.expiresAt,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

/** The stored session, or null when absent/expired/corrupt. */
export function loadGuestSession(): GuestSession | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as GuestSession;
    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.guestId !== 'string' ||
      new Date(parsed.expiresAt).getTime() <= Date.now()
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function clearGuestSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(PROMPT_KEY);
}

/** Homepage → guest console prompt handoff (sent as turn 1 on arrival). */
export function stashPendingPrompt(prompt: string): void {
  sessionStorage.setItem(PROMPT_KEY, prompt);
}

export function takePendingPrompt(): string | null {
  const prompt = sessionStorage.getItem(PROMPT_KEY);
  sessionStorage.removeItem(PROMPT_KEY);
  return prompt;
}

/** Guest-scoped session id accepted by the proxy (`guest-` + 32 hex). */
export function newGuestChatSessionId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `guest-${hex}`;
}
